// Copyright (c) 2026 SpicyFriends
import { Button, Popover, PopoverContent, PopoverDescription, PopoverTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { isHelpDismissed, useSetHelpDismissed } from '@web/src/features/help/help-utils'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { XIcon } from 'lucide-react'
import {
  type ComponentProps,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

type DismissHelpPopoverProps = {
  children?: ReactNode
  helpType: string
  content: ReactNode
  /** When false, the popover stays closed. Dismissal state is unchanged. */
  showPopover?: boolean
  contentClassName?: string
  descriptionClassName?: string
  closeButtonClassName?: string
  positionerClassName?: string
  side?: 'top' | 'bottom' | 'left' | 'right' | 'inline-end' | 'inline-start'
  sideOffset?: number
  align?: 'start' | 'center' | 'end'
  wrapperProps?: ComponentProps<'div'>
}

const DismissHelpPopover = ({
  children,
  helpType,
  content,
  showPopover = true,
  contentClassName,
  descriptionClassName,
  closeButtonClassName,
  positionerClassName,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  wrapperProps,
}: DismissHelpPopoverProps) => {
  const { t } = useTranslation()
  const { currentUser, isAuthenticated, refreshSession } = useAuth()
  const { dismissHelp } = useSetHelpDismissed()
  const [isDismissed, setIsDismissed] = useState(true)
  const dismissedLocallyRef = useRef(false)

  useEffect(() => {
    if (dismissedLocallyRef.current) {
      setIsDismissed(true)
      return
    }
    if (isAuthenticated) {
      setIsDismissed(isHelpDismissed(currentUser, helpType))
      return
    }
    setIsDismissed(true)
  }, [currentUser, helpType, isAuthenticated])

  const handleDismiss = async () => {
    if (dismissedLocallyRef.current || isDismissed || !showPopover) {
      return
    }
    dismissedLocallyRef.current = true
    setIsDismissed(true)
    await dismissHelp(helpType)
    await refreshSession()
  }

  const shouldShowPopover = showPopover && !isDismissed && isAuthenticated

  const { className: wrapperClassName, ...restWrapper } = wrapperProps ?? {}

  return (
    <div className={cn(wrapperClassName)} {...restWrapper}>
      {shouldShowPopover ? (
        <Popover
          onOpenChange={(open) => {
            if (!open) {
              handleDismiss()
            }
          }}
          open
        >
          {isValidElement(children) ? (
            <PopoverTrigger render={children as ReactElement} />
          ) : (
            <PopoverTrigger nativeButton={false} render={<span className="flex w-full" />}>
              {children}
            </PopoverTrigger>
          )}
          <PopoverContent
            align={align}
            className={contentClassName}
            initialFocus={false}
            positionerClassName={positionerClassName}
            side={side}
            sideOffset={sideOffset}
          >
            <div className="flex items-start gap-1">
              <PopoverDescription className={cn('font-medium text-sm', descriptionClassName)}>
                {content}
              </PopoverDescription>
              <Button
                aria-label={t('Dismiss')}
                className={cn('-mt-1 -mr-1 shrink-0', closeButtonClassName)}
                onClick={(event) => {
                  event.stopPropagation()
                  handleDismiss()
                }}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <XIcon className="size-3.5" />
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        children
      )}
    </div>
  )
}

export default DismissHelpPopover
